import {
  MappedEntity,
  mapEntity,
  ScenariosRequest,
  ScenariosResponse,
  scenario,
} from '@api';
import { useQueryClient } from '@tanstack/react-query';
import { config } from '@terminal/config';
import {
  ApiStreamProps,
  ApiStreamStatus,
  apiStream,
} from '@terminal/utils/api-stream/apiStream';
import { useScenarioStore } from '@terminal/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import { useCallback, useRef, useState } from 'react';

import { createEntityQueryOptions } from '../entities/useEntities.utils';

export interface ScenariosData extends Omit<ScenariosResponse, 'entity'> {
  entity: MappedEntity;
}

export const useScenarios = () => {
  const [data, setData] = useState<ScenariosData[]>([]);
  const [lastBody, setLastBody] = useState<ScenariosRequest>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const controllerRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();
  const setLastScenariosData = useScenarioStore(
    state => state.setLastScenariosData
  );
  const setHasExceededUsageLimit = useScenarioStore(
    state => state.setHasExceededUsageLimit
  );

  const { path } = scenario.scenarios;

  const handleStreamData = useCallback(
    async (chunks: ScenariosResponse[]) => {
      const tags = chunks.map(c => c.entity);
      const entityPromises = tags.map(tag =>
        queryClient.fetchQuery(createEntityQueryOptions(tag))
      );
      const entities = await Promise.all(entityPromises);
      const data = entities.reduce<ScenariosData[]>((res, next, idx) => {
        if (next.data) {
          res.push({ ...chunks[idx], entity: mapEntity(next.data) });
        }

        return res;
      }, []);

      setData(data);
      setLastScenariosData(data);
    },
    [queryClient, setLastScenariosData]
  );

  const onComplete = useCallback((status: ApiStreamStatus) => {
    switch (status) {
      case ApiStreamStatus.Success:
        setIsLoading(false);
        setError(false);
        break;
      case ApiStreamStatus.Error:
        setIsLoading(false);
        setError(true);
        break;
      case ApiStreamStatus.Abort:
        setIsLoading(false);
        setError(false);
        break;
    }
  }, []);

  const sendMessage = useCallback(
    async (body: ScenariosRequest) => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      const abortController = new AbortController();
      controllerRef.current = abortController;

      // Reset state variables
      setData([]);
      setIsLoading(true);
      setError(false);
      setLastBody(body);

      const apiStreamProps: ApiStreamProps<ScenariosResponse> = {
        body,
        onReadChunk: handleStreamData,
        onComplete: () => onComplete(ApiStreamStatus.Success),
        onError: error => {
          const isUsageLimitExceeded = error?.status === 429;

          if (isUsageLimitExceeded) {
            setHasExceededUsageLimit(true);
          }

          onComplete(ApiStreamStatus.Error);
        },
        onAbort: () => onComplete(ApiStreamStatus.Abort),
        controller: abortController,
        endpoint: `${config.api.root}${path}`,
      };

      await apiStream<ScenariosResponse>(apiStreamProps);
    },
    [handleStreamData, onComplete, path, setHasExceededUsageLimit]
  );

  const refetch = useCallback(() => {
    if (lastBody) {
      sendMessage(lastBody);
    }
  }, [lastBody, sendMessage]);

  const clear = useCallback(() => {
    setData([]);
  }, []);

  const abort = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    setIsLoading(false);
  }, []);

  return {
    isLoading,
    error,
    sendMessage,
    refetch,
    abort,
    clear,
    data,
  };
};
